import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { useImports } from "../../api/queries";
import { ICON } from "../../components/ui";
import { etaText, importEta } from "../../lib/importEta";

// While the AI reads a menu, a bar at the bottom of every screen says what it is doing, how far it is and about
// how long is left (owner, 2026-10-22: without it people think it froze and give up). It follows the import
// already polled by useImports; nothing is started or spent from here.

const ACTIVE = new Set(["uploading", "queued", "processing"]);

/** What the AI does while it reads a page, shown in turn (the steps are real: see import/provider.ts). */
const READING_STEPS = [
  "Citește fotografiile paginii",
  "Extrage preparatele, gramajele și prețurile",
  "Recitește fiecare zonă mărită, ca să nu scape nimic",
  "Verifică fiecare preț cu o a doua citire",
  "Compară citirile: păstrează doar ce se confirmă",
];
const STEP_MS = 3_500;

export function ImportProgressDock({ rid }: { rid: string }) {
  const jobs = useImports(rid);
  const location = useLocation();
  const [now, setNow] = useState(() => Date.now());
  const job = jobs.data?.find((j) => ACTIVE.has(j.status) || (j.status === "ready" && !j.menuId && !j.error));

  useEffect(() => {
    if (!job) return;
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [job]);

  if (!job) return null;
  const eta = importEta(job, now);
  const reading = job.status === "processing" && eta.done < eta.total;
  const step = READING_STEPS[Math.floor(now / STEP_MS) % READING_STEPS.length] as string;
  const title =
    job.status === "uploading"
      ? "Se încarcă paginile…"
      : job.status === "queued"
        ? "Pornește analiza meniului…"
        : reading
          ? "AI-ul analizează meniul"
          : "Pregătește ciorna meniului…";
  const detail = reading
    ? `Pagina ${Math.min(eta.total, eta.done + Math.max(1, eta.reading))} din ${eta.total} · ${step}`
    : job.status === "processing" || job.status === "ready"
      ? "Pune preparatele în categorii și marchează ce trebuie verificat"
      : "Nu închide aplicația până se termină încărcarea.";
  const progress = eta.total
    ? Math.round(((eta.done + (reading ? 0.5 * eta.reading : 0)) / eta.total) * 100)
    : 0;

  return (
    <section className="import-dock" role="status" aria-live="polite" aria-label="Analiza meniului">
      <span className="import-dock-icon" aria-hidden="true">
        <Sparkles {...ICON} />
      </span>
      <div className="import-dock-body">
        <div className="import-dock-head">
          <strong>
            {title}
            <span className="import-dock-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </strong>
          <span className="import-dock-eta">{etaText(eta.remainingMs)}</span>
        </div>
        <span className="import-dock-detail" key={reading ? step : title}>
          {detail}
        </span>
        <div
          className="progress import-dock-bar"
          role="progressbar"
          aria-label="Progresul analizei"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div style={{ width: `${Math.max(4, progress)}%` }} />
        </div>
      </div>
      {location.pathname !== "/app/imports" && (
        <Link className="import-dock-link" to="/app/imports">
          Detalii
        </Link>
      )}
    </section>
  );
}
